import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { guardATC } from './wroe-atc-guard.js';
import { PROFANITY_LIST } from './wroe-profanity-list.js';

const PET_NAME_PATTERN = /^[a-zA-Z]+$/;

/**
 * @typedef {object} ThemeGlobal
 * @property {{ cart_add_url?: string }=} routes
 * @property {{ added?: string }=} translations
 */

/**
 * @typedef {object} ToyColorImage
 * @property {string} key
 * @property {string} label
 * @property {string} url
 */

/**
 * @typedef {object} VariantPreviewConfig
 * @property {boolean} available
 * @property {ToyColorImage[]} toyColors
 */

/**
 * @typedef {object} PreviewConfig
 * @property {Record<string, unknown>} profile
 * @property {Record<string, VariantPreviewConfig>} variants
 */

/**
 * @typedef {object} WroePreviewEngineLike
 * @property {() => Promise<void>} init
 * @property {() => void} refresh
 * @property {(color: string) => void} updateColor
 * @property {(url: string) => Promise<void>} updateBaseImage
 * @property {() => void} clearBaseImage
 * @property {(text: string) => void} updatePetName
 * @property {() => void} destroy
 */

/**
 * @typedef {object} WroeRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} titleText
 * @property {HTMLInputElement} petNameInput
 * @property {HTMLElement} petNameCheck
 * @property {HTMLElement} charCounter
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} liveRegion
 * @property {HTMLCanvasElement} previewCanvas
 * @property {HTMLElement} previewStatus
 * @property {HTMLElement} contentScroll
 *
 * @extends {DialogComponent}
 */
class WroePersonalizationComponent extends DialogComponent {
  requiredRefs = [
    'dialog',
    'titleText',
    'petNameInput',
    'petNameCheck',
    'charCounter',
    'addToCartButton',
    'errorMessage',
    'successMessage',
    'liveRegion',
    'previewCanvas',
    'previewStatus',
    'contentScroll',
  ];

  #abortController = new AbortController();

  /** @type {Window & { Theme?: ThemeGlobal }} */
  #windowWithTheme = /** @type {Window & { Theme?: ThemeGlobal }} */ (window);

  /** @type {(() => Promise<void>) | null} */
  #parentCloseDialog = null;

  /** @type {WroePreviewEngineLike | null} */
  #previewEngine = null;

  /** @type {Promise<void> | null} */
  #previewInitPromise = null;

  /** @type {PreviewConfig | null} */
  #previewConfig = null;

  /** @type {string} */
  #selectedVariantId = '';

  /** @type {boolean} */
  #variantAvailable = false;

  /** @type {{ petName: string, embroideryColor: string, embroideryColorImageUrl: string, embroideryPreviewColor: string, toyColor: string, toyColorKey: string, toyColorSwatchUrl: string, toyColorPreviewUrl: string }} */
  #state = {
    petName: '',
    embroideryColor: '',
    embroideryColorImageUrl: '',
    embroideryPreviewColor: '#000000',
    toyColor: '',
    toyColorKey: '',
    toyColorSwatchUrl: '',
    toyColorPreviewUrl: '',
  };

  /** @type {Map<string, string>} */
  #sampledColorCache = new Map();

  /** @type {HTMLElement[]} */
  #carouselTracks = [];

  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;

  /** @type {boolean} */
  #isDirty = false;

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {boolean} */
  #resetOnNextOpen = false;

  /** @type {number} */
  #previewLoadRequestId = 0;

  connectedCallback() {
    this.#parentCloseDialog = this.closeDialog;
    this.closeDialog = this.#animatedCloseDialog;
    super.connectedCallback();

    this.#readPreviewConfig();
    this.#syncVariantFromForm();
    this.#initCarousels();
    this.#bindVariantUpdates();
    this.#syncInputAndTitle();
    this.#updateToyColorAvailability();
    void this.#applyDefaultSelections();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#previewEngine?.destroy();
    this.#previewEngine = null;
    this.#previewInitPromise = null;
  }

  showDialog() {
    this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#clearFeedback();
    this.#syncVariantFromForm();

    if (this.#resetOnNextOpen) {
      this.#resetCustomization();
      this.#resetOnNextOpen = false;
    }

    this.#updateToyColorAvailability();
    void this.#applyDefaultSelections();
    this.#syncInputAndTitle();
    this.#updateAddToCartState();

    super.showDialog();
    this.#focusModalTitle();
    void this.#ensurePreviewReady();
  }

  /** @param {Event} event */
  handleLauncherClick = (event) => {
    event.preventDefault();
    this.showDialog();
  };

  /** @param {Event} event */
  handlePetNameInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const sanitized = event.target.value
      .replace(/[^a-zA-Z]/g, '')
      .slice(0, this.#maxPetNameLength);

    event.target.value = sanitized;
    this.#state.petName = sanitized;
    this.#isDirty = this.#isDirty || sanitized.length > 0;
    this.#syncInputAndTitle();
    this.#previewEngine?.updatePetName(sanitized);

    if (sanitized && this.#isPetNameProfane(sanitized)) {
      this.#setError('Please choose a different pet name.', 'petName');
    } else {
      this.#clearFeedback();
    }

    this.#updateAddToCartState();
  };

  /** @param {Event} event */
  selectEmbroideryColor = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const option = target.closest('[data-wroe-select="embroideryColor"]');
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    await this.#selectEmbroideryOption(option);
  };

  /** @param {Event} event */
  selectToyColor = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const option = target.closest('[data-wroe-select="toyColor"]');
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    await this.#selectToyColorOption(option);
  };

  /** @param {Event} event */
  selectSize = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const option = target.closest('[data-wroe-select="size"]');
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    const variantId = option.dataset.variantId || '';
    const variantConfig = this.#previewConfig?.variants?.[variantId];
    if (!variantId || !variantConfig?.available) {
      this.#setError('Please select an available size.', 'size');
      return;
    }

    const previousToyColorKey = this.#state.toyColorKey;
    this.#selectedVariantId = variantId;
    this.#variantAvailable = true;
    this.dataset.selectedVariantId = variantId;
    this.#syncProductFormVariantInput();
    this.#updateSelectionUI('size', variantId);
    this.#updateToyColorAvailability();
    this.#isDirty = true;

    if (previousToyColorKey && this.#state.toyColorKey === previousToyColorKey) {
      await this.#loadSelectedToyPreview();
      this.#clearFeedback();
    } else if (previousToyColorKey) {
      this.#setError('That Toy Color is not configured for the selected size. Please choose another color.', 'toyColor');
    } else {
      await this.#applyDefaultToyColor();
      this.#clearFeedback();
    }

    this.#updateAddToCartState();
  };

  /** @param {Event} event */
  carouselPrev = (event) => {
    event.preventDefault();
    this.#scrollCarousel(event, -1);
  };

  /** @param {Event} event */
  carouselNext = (event) => {
    event.preventDefault();
    this.#scrollCarousel(event, 1);
  };

  handleAddToCart = guardATC(async (/** @type {Event} */ event) => {
    event.preventDefault();
    this.#clearFeedback();
    this.#syncVariantFromForm();
    this.#updateToyColorAvailability();

    if (!this.#validate()) return;

    this.#isSubmitting = true;
    const button = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('id', this.#selectedVariantId);
    formData.append('quantity', '1');
    formData.append('properties[Pet Name]', this.#state.petName);
    formData.append('properties[Embroidery Color]', this.#state.embroideryColor);
    formData.append('properties[Toy Color]', this.#state.toyColor);

    if (this.#state.embroideryColorImageUrl) {
      formData.append('properties[_wroe_embroidery_color_swatch]', this.#state.embroideryColorImageUrl);
    }
    if (this.#state.toyColorSwatchUrl) {
      formData.append('properties[_wroe_toy_color_swatch]', this.#state.toyColorSwatchUrl);
    }
    formData.append('properties[_wroe_personalization_version]', '2');

    /** @type {string[]} */
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId || '')
      .filter(Boolean);

    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const addUrl = this.#windowWithTheme.Theme?.routes?.cart_add_url || '/cart/add.js';
    const request = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(addUrl, {
        ...request,
        headers: {
          ...request.headers,
          Accept: 'text/html',
        },
      });
      const payload = await response.json();

      if (payload.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, payload.message, payload.description, payload.errors));
        this.#setError(payload.message || 'Unable to add this personalized product right now.');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.#selectedVariantId, {
          source: 'wroe-personalization',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: payload.sections,
        })
      );

      const success = /** @type {HTMLElement} */ (this.refs.successMessage);
      success.textContent = 'Added to cart.';
      success.classList.remove('hidden');
      const liveRegion = /** @type {HTMLElement} */ (this.refs.liveRegion);
      liveRegion.textContent = this.#windowWithTheme.Theme?.translations?.added || 'Added';

      this.#isDirty = false;
      this.#resetOnNextOpen = true;
      await this.closeDialog();
      this.#animateCartAddition();
      this.#ensureLauncherEnabled();
    } catch (error) {
      console.error(error);
      this.#ensureLauncherEnabled();
      this.#setError('Unable to add this personalized product right now.');
    } finally {
      this.#isSubmitting = false;
      button.removeAttribute('aria-busy');
      this.#updateAddToCartState();
    }
  });

  #bindVariantUpdates() {
    const scope = this.closest('.shopify-section, dialog');
    if (!scope) return;

    scope.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  /** @param {CustomEvent} event */
  #handleVariantUpdate = (event) => {
    const productId = `${event.detail?.data?.productId || ''}`;
    if (productId && productId !== `${this.dataset.productId || ''}`) return;

    const resource = event.detail?.resource;
    this.#selectedVariantId = `${resource?.id || ''}`;
    this.#variantAvailable = Boolean(resource?.available);
    if (this.#selectedVariantId) this.dataset.selectedVariantId = this.#selectedVariantId;
    this.#updateSelectionUI('size', this.#selectedVariantId);

    const previousToyColorKey = this.#state.toyColorKey;
    this.#updateToyColorAvailability();

    if (previousToyColorKey && this.#state.toyColorKey === previousToyColorKey) {
      void this.#loadSelectedToyPreview();
    } else if (previousToyColorKey) {
      this.#setError('That Toy Color is not configured for the selected size. Please choose another color.', 'toyColor');
    } else {
      void this.#applyDefaultToyColor();
    }

    this.#updateAddToCartState();
  };

  #readPreviewConfig() {
    const element = this.#previewConfigElement;
    if (!element) {
      this.#previewConfig = { profile: {}, variants: {} };
      return;
    }

    try {
      this.#previewConfig = JSON.parse(element.textContent || '{}');
    } catch (error) {
      console.error('[WroePersonalization] Invalid preview configuration.', error);
      this.#previewConfig = { profile: {}, variants: {} };
    }
  }

  #syncVariantFromForm() {
    const form = this.#productForm;
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    this.#selectedVariantId = variantInput?.value || this.dataset.selectedVariantId || '';

    const variantConfig = this.#selectedVariantConfig;
    this.#variantAvailable = variantConfig?.available ?? Boolean(this.#selectedVariantId);
    this.#updateSelectionUI('size', this.#selectedVariantId);
  }

  #syncProductFormVariantInput() {
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.#productForm?.querySelector('input[name="id"]') || null
    );
    if (variantInput) variantInput.value = this.#selectedVariantId;
  }

  #updateToyColorAvailability() {
    const variant = this.#selectedVariantConfig;
    const availableKeys = new Set((variant?.toyColors || []).map((item) => item.key));
    const options = this.querySelectorAll('[data-wroe-select="toyColor"]');

    for (const node of options) {
      if (!(node instanceof HTMLButtonElement)) continue;
      const enabled = this.#variantAvailable && availableKeys.has(node.dataset.colorKey || '');
      node.disabled = !enabled;
      node.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    }

    if (this.#state.toyColorKey) {
      const match = this.#findToyColor(this.#state.toyColorKey);
      if (match) {
        this.#state.toyColorPreviewUrl = match.url;
      } else {
        this.#clearToyColorSelection();
      }
    }

    if (this.#hasFabricColors && availableKeys.size === 0) {
      this.#setPreviewStatus('Preview unavailable for this size. Check its Toy Color images.', true);
    }

    this.#refreshCarouselButtons();
  }

  async #applyDefaultSelections() {
    const firstEmbroidery = this.#hasEmbroideryColors
      ? this.querySelector('[data-wroe-select="embroideryColor"]')
      : null;

    if (!this.#state.embroideryColor && firstEmbroidery instanceof HTMLButtonElement) {
      await this.#selectEmbroideryOption(firstEmbroidery, false);
    }

    await this.#applyDefaultToyColor();
    this.#updateAddToCartState();
  }

  async #applyDefaultToyColor() {
    if (!this.#hasFabricColors || this.#state.toyColorKey) return;

    const firstAvailable = Array.from(this.querySelectorAll('[data-wroe-select="toyColor"]'))
      .find((node) => node instanceof HTMLButtonElement && !node.disabled);

    if (firstAvailable instanceof HTMLButtonElement) {
      await this.#selectToyColorOption(firstAvailable, false);
    }
  }

  /**
   * @param {HTMLButtonElement} option
   * @param {boolean} [markDirty=true]
   */
  async #selectEmbroideryOption(option, markDirty = true) {
    const value = option.dataset.value || '';
    if (!value) return;

    this.#state.embroideryColor = value;
    this.#state.embroideryColorImageUrl = option.dataset.imageUrl || '';
    this.#updateSelectionUI('embroideryColor', value);
    if (markDirty) this.#isDirty = true;

    const previewColor = await this.#resolvePreviewColor(
      option.dataset.previewColor || value,
      option.dataset.imageUrl || ''
    );

    if (this.#state.embroideryColor !== value) return;
    this.#state.embroideryPreviewColor = previewColor;
    this.#previewEngine?.updateColor(previewColor);
    this.#clearFeedback();
    this.#updateAddToCartState();
  }

  /**
   * @param {HTMLButtonElement} option
   * @param {boolean} [markDirty=true]
   */
  async #selectToyColorOption(option, markDirty = true) {
    const key = option.dataset.colorKey || '';
    const value = option.dataset.value || '';
    const match = this.#findToyColor(key);

    if (!key || !value || !match) {
      this.#setError('This Toy Color is not configured for the selected size.', 'toyColor');
      return;
    }

    this.#state.toyColor = value;
    this.#state.toyColorKey = key;
    this.#state.toyColorSwatchUrl = option.dataset.imageUrl || '';
    this.#state.toyColorPreviewUrl = match.url;
    this.#updateSelectionUI('toyColor', value);
    if (markDirty) this.#isDirty = true;

    this.#clearFeedback();
    await this.#loadSelectedToyPreview();
    this.#updateAddToCartState();
  }

  async #loadSelectedToyPreview() {
    const url = this.#state.toyColorPreviewUrl;
    if (!url) {
      this.#previewLoadRequestId += 1;
      this.#previewEngine?.clearBaseImage();
      this.#setPreviewStatus('Choose a Toy Color to see your preview.');
      return;
    }

    if (!this.#previewEngine) return;

    this.#setPreviewStatus('Loading your preview…');
    const requestId = ++this.#previewLoadRequestId;
    try {
      await this.#previewEngine.updateBaseImage(url);
      if (requestId !== this.#previewLoadRequestId) return;
      this.#setPreviewStatus('');
    } catch (error) {
      if (requestId !== this.#previewLoadRequestId) return;
      console.error('[WroePersonalization] Toy Color preview failed to load.', error);
      this.#setPreviewStatus('We could not load this preview. Please choose another Toy Color.', true);
    }
  }

  /** @param {string} key */
  #findToyColor(key) {
    return this.#selectedVariantConfig?.toyColors?.find((item) => item.key === key) || null;
  }

  #clearToyColorSelection() {
    this.#previewLoadRequestId += 1;
    this.#state.toyColor = '';
    this.#state.toyColorKey = '';
    this.#state.toyColorSwatchUrl = '';
    this.#state.toyColorPreviewUrl = '';
    this.#updateSelectionUI('toyColor', '');
    this.#previewEngine?.clearBaseImage();
  }

  /**
   * @param {'size' | 'embroideryColor' | 'toyColor'} field
   * @param {string} value
   */
  #updateSelectionUI(field, value) {
    const options = this.querySelectorAll(`[data-wroe-select="${field}"]`);
    for (const node of options) {
      if (!(node instanceof HTMLButtonElement)) continue;
      const selected = node.dataset.value === value;
      node.classList.toggle('is-selected', selected);
      node.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
  }

  #syncInputAndTitle() {
    const input = /** @type {HTMLInputElement} */ (this.refs.petNameInput);
    if (input.value !== this.#state.petName) input.value = this.#state.petName;

    const validName = this.#isPetNameValid;
    const check = /** @type {HTMLElement} */ (this.refs.petNameCheck);
    check.classList.toggle('is-visible', validName);

    const counter = /** @type {HTMLElement} */ (this.refs.charCounter);
    counter.textContent = `Up to ${this.#maxPetNameLength} characters.`;

    const title = /** @type {HTMLElement} */ (this.refs.titleText);
    const enteredName = this.#state.petName.trim();
    const displayName = enteredName
      ? enteredName.charAt(0).toUpperCase() + enteredName.slice(1)
      : 'Your Pup';
    title.textContent = `${displayName}'s 1 of 1`;
  }

  #validate() {
    if (!this.#selectedVariantId || !this.#variantAvailable) {
      this.#setError('Please select an available size.');
      return false;
    }
    if (!this.#hasEmbroideryColors) {
      this.#setError('Embroidery Color options are not configured for this product.');
      return false;
    }
    if (!this.#hasFabricColors) {
      this.#setError('Toy Color options are not configured for this product.');
      return false;
    }
    if (!this.#state.petName) {
      this.#setError('Please enter your pup’s name.', 'petName');
      return false;
    }
    if (!this.#isPetNameValid) {
      this.#setError(
        this.#isPetNameProfane(this.#state.petName)
          ? 'Please choose a different pet name.'
          : 'Use letters only for your pup’s name.',
        'petName'
      );
      return false;
    }
    if (this.#hasEmbroideryColors && !this.#state.embroideryColor) {
      this.#setError('Please select an Embroidery Color.', 'embroideryColor');
      return false;
    }
    if (this.#hasFabricColors && (!this.#state.toyColor || !this.#state.toyColorPreviewUrl)) {
      this.#setError('Please select a Toy Color available for this size.', 'toyColor');
      return false;
    }

    this.#clearFeedback();
    return true;
  }

  #updateAddToCartState() {
    const hasEmbroidery = this.#hasEmbroideryColors && Boolean(this.#state.embroideryColor);
    const hasToyColor = this.#hasFabricColors && Boolean(this.#state.toyColor && this.#state.toyColorPreviewUrl);
    const canSubmit = Boolean(
      this.#selectedVariantId &&
      this.#variantAvailable &&
      this.#isPetNameValid &&
      hasEmbroidery &&
      hasToyColor &&
      !this.#isSubmitting
    );

    const button = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);
    button.disabled = !canSubmit;
  }

  /**
   * @param {string} message
   * @param {string} [focusGroup]
   */
  #setError(message, focusGroup) {
    const success = /** @type {HTMLElement} */ (this.refs.successMessage);
    success.classList.add('hidden');

    const error = /** @type {HTMLElement} */ (this.refs.errorMessage);
    error.textContent = message;
    error.classList.remove('hidden');

    const liveRegion = /** @type {HTMLElement} */ (this.refs.liveRegion);
    liveRegion.textContent = message;

    if (!focusGroup) return;
    const group = this.querySelector(`[data-wroe-group="${focusGroup}"]`);
    group?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const control = group?.querySelector('input, button:not(:disabled)');
    if (control instanceof HTMLElement) control.focus({ preventScroll: true });
  }

  #clearFeedback() {
    const error = /** @type {HTMLElement} */ (this.refs.errorMessage);
    const success = /** @type {HTMLElement} */ (this.refs.successMessage);
    const liveRegion = /** @type {HTMLElement} */ (this.refs.liveRegion);
    error.textContent = '';
    error.classList.add('hidden');
    success.textContent = '';
    success.classList.add('hidden');
    liveRegion.textContent = '';
  }

  /**
   * @param {string} message
   * @param {boolean} [isError=false]
   */
  #setPreviewStatus(message, isError = false) {
    const status = /** @type {HTMLElement} */ (this.refs.previewStatus);
    status.classList.toggle('hidden', !message);
    status.classList.toggle('is-error', Boolean(message) && isError);
    const text = status.querySelector('span');
    if (text) text.textContent = message;
  }

  async #ensurePreviewReady(attempt = 0) {
    const dialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);
    const canvas = /** @type {HTMLCanvasElement} */ (this.refs.previewCanvas);
    const rect = canvas.getBoundingClientRect();

    if (!dialog.open || rect.width <= 2 || rect.height <= 2) {
      if (attempt >= 12) return;
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      return this.#ensurePreviewReady(attempt + 1);
    }

    await this.#initPreviewEngine();
    this.#previewEngine?.refresh();
    this.#refreshCarouselButtons();
    await this.#syncPreviewFromState();
  }

  async #initPreviewEngine() {
    if (this.#previewEngine) return;
    if (this.#previewInitPromise) return this.#previewInitPromise;

    this.#previewInitPromise = (async () => {
      const engineUrl = this.dataset.previewEngineUrl;
      if (!engineUrl) throw new Error('Preview engine URL is missing.');

      const dynamicImport = /** @type {(url: string) => Promise<{ WroePreviewEngine: new (canvas: HTMLCanvasElement, config: unknown) => WroePreviewEngineLike }>} */ (
        new Function('url', 'return import(url);')
      );
      const module = await dynamicImport(engineUrl);
      const canvas = /** @type {HTMLCanvasElement} */ (this.refs.previewCanvas);
      const engine = new module.WroePreviewEngine(canvas, {
        profile: this.#previewConfig?.profile || {},
        fontUrl: this.dataset.previewFontUrl || '',
      });
      await engine.init();
      this.#previewEngine = engine;
    })();

    try {
      await this.#previewInitPromise;
    } catch (error) {
      console.error('[WroePersonalization] Preview engine failed to initialize.', error);
      this.#setPreviewStatus('Your choices are saved, but the visual preview is unavailable.', true);
    } finally {
      this.#previewInitPromise = null;
    }
  }

  async #syncPreviewFromState() {
    if (!this.#previewEngine) return;
    this.#previewEngine.updatePetName(this.#state.petName);
    this.#previewEngine.updateColor(this.#state.embroideryPreviewColor || '#000000');
    await this.#loadSelectedToyPreview();
  }

  #initCarousels() {
    this.#carouselTracks = Array.from(this.querySelectorAll('[data-wroe-carousel-track]'))
      .filter((node) => node instanceof HTMLElement);

    for (const track of this.#carouselTracks) {
      track.addEventListener('scroll', () => this.#updateCarouselButtons(track), {
        signal: this.#abortController.signal,
      });
    }

    window.addEventListener('resize', () => this.#refreshCarouselButtons(), {
      signal: this.#abortController.signal,
    });
    this.#refreshCarouselButtons();
  }

  /**
   * @param {Event} event
   * @param {-1 | 1} direction
   */
  #scrollCarousel(event, direction) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const carousel = target.closest('[data-wroe-carousel]');
    const track = carousel?.querySelector('[data-wroe-carousel-track]');
    if (!(track instanceof HTMLElement)) return;

    const amount = Math.max(165, Math.floor(track.clientWidth * 0.82));
    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    window.setTimeout(() => this.#updateCarouselButtons(track), 240);
  }

  #refreshCarouselButtons() {
    for (const track of this.#carouselTracks) this.#updateCarouselButtons(track);
  }

  /** @param {HTMLElement} track */
  #updateCarouselButtons(track) {
    const carousel = track.closest('[data-wroe-carousel]');
    const previous = carousel?.querySelector('[data-wroe-carousel-prev]');
    const next = carousel?.querySelector('[data-wroe-carousel-next]');
    if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const canScroll = maxScroll > 4;
    previous.disabled = !canScroll || track.scrollLeft <= 2;
    next.disabled = !canScroll || track.scrollLeft >= maxScroll - 2;
  }

  /**
   * @param {string} candidate
   * @param {string} imageUrl
   */
  async #resolvePreviewColor(candidate, imageUrl) {
    const normalized = candidate.trim();
    if (normalized && typeof CSS !== 'undefined' && CSS.supports('color', normalized)) return normalized;
    if (!imageUrl) return '#000000';

    const cached = this.#sampledColorCache.get(imageUrl);
    if (cached) return cached;

    return new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (!context) return resolve('#000000');

          canvas.width = 16;
          canvas.height = 16;
          context.drawImage(image, 0, 0, 16, 16);
          const pixels = context.getImageData(0, 0, 16, 16).data;
          let red = 0;
          let green = 0;
          let blue = 0;
          let count = 0;

          for (let index = 0; index < pixels.length; index += 4) {
            const alpha = pixels[index + 3];
            if (alpha === undefined || alpha < 20) continue;
            red += pixels[index] || 0;
            green += pixels[index + 1] || 0;
            blue += pixels[index + 2] || 0;
            count += 1;
          }

          if (!count) return resolve('#000000');
          const color = `rgb(${Math.round(red / count)}, ${Math.round(green / count)}, ${Math.round(blue / count)})`;
          this.#sampledColorCache.set(imageUrl, color);
          resolve(color);
        } catch {
          resolve('#000000');
        }
      };
      image.onerror = () => resolve('#000000');
      image.src = imageUrl;
    });
  }

  /** @param {string} value */
  #isPetNameProfane(value) {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (PROFANITY_LIST.has(normalized)) return true;

    for (const term of PROFANITY_LIST) {
      if (term.length >= 4 && normalized.includes(term)) return true;
    }
    return false;
  }

  #resetCustomization() {
    this.#state.petName = '';
    this.#state.embroideryColor = '';
    this.#state.embroideryColorImageUrl = '';
    this.#state.embroideryPreviewColor = '#000000';
    this.#clearToyColorSelection();
    this.#updateSelectionUI('embroideryColor', '');
    this.#previewEngine?.updateColor('#000000');
    this.#previewEngine?.updatePetName('');
    this.#syncInputAndTitle();
    this.#clearFeedback();
    this.#isDirty = false;
  }

  #animatedCloseDialog = async () => {
    const dialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);
    if (!dialog?.open) return;

    if (this.#isDirty && !this.#isSubmitting) {
      const shouldClose = window.confirm('You have unsaved personalization changes. Close anyway?');
      if (!shouldClose) return;
    }

    this.#isDirty = false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      await this.#parentCloseDialog?.();
      this.#restoreLauncherFocus();
      return;
    }

    dialog.classList.add('is-closing');
    await new Promise((resolve) => {
      dialog.addEventListener('animationend', resolve, { once: true });
      window.setTimeout(resolve, 300);
    });
    dialog.classList.remove('is-closing');
    await this.#parentCloseDialog?.();
    this.#restoreLauncherFocus();
  };

  #focusModalTitle() {
    const title = this.querySelector('[id^="wroePersonalizationTitle-"]');
    if (title instanceof HTMLElement) title.focus();
  }

  #restoreLauncherFocus() {
    if (this.#lastFocusedElement instanceof HTMLElement) this.#lastFocusedElement.focus();
  }

  #ensureLauncherEnabled() {
    const enable = () => {
      const launcher = /** @type {HTMLButtonElement | null} */ (
        this.#productForm?.querySelector('[data-personalization-launcher="true"]')
      );
      if (launcher && this.#selectedVariantId) launcher.disabled = false;
    };
    enable();
    window.requestAnimationFrame(enable);
  }

  #animateCartAddition() {
    const addToCart = /** @type {HTMLElement & { animateAddToCart?: () => void, refs?: { addToCartButton?: HTMLElement } } | null} */ (
      this.#productForm?.querySelector('add-to-cart-component') || null
    );
    if (!addToCart || addToCart.dataset.addToCartAnimation !== 'true') return;
    addToCart.animateAddToCart?.();
  }

  get #selectedVariantConfig() {
    return this.#previewConfig?.variants?.[this.#selectedVariantId] || null;
  }

  get #hasEmbroideryColors() {
    return this.dataset.hasEmbroideryColors === 'true';
  }

  get #hasFabricColors() {
    return this.dataset.hasFabricColors === 'true';
  }

  get #maxPetNameLength() {
    const value = Number(this.dataset.maxPetNameLength);
    return Number.isFinite(value) && value > 0 ? value : 12;
  }

  get #isPetNameValid() {
    return Boolean(
      this.#state.petName &&
      PET_NAME_PATTERN.test(this.#state.petName) &&
      !this.#isPetNameProfane(this.#state.petName)
    );
  }

  /** @returns {HTMLElement | null} */
  get #productForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const section = this.closest('.shopify-section');
    return section?.querySelector(`product-form-component[data-product-id="${productId}"]`) || null;
  }

  /** @returns {HTMLScriptElement | null} */
  get #previewConfigElement() {
    const id = this.dataset.previewConfigId;
    if (!id) return null;
    return /** @type {HTMLScriptElement | null} */ (document.getElementById(id));
  }
}

if (!customElements.get('wroe-personalization-component')) {
  customElements.define('wroe-personalization-component', WroePersonalizationComponent);
}
